
import { useState, useMemo } from "react";
import { Package, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AppSearchInput } from "./AppSearchInput";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DeviceAppsAllAppsContentProps {
  deviceId: string;
}

export const DeviceAppsAllAppsContent = ({ deviceId }: DeviceAppsAllAppsContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  console.log('[DeviceAppsAllAppsContent] Loading apps for device:', deviceId);
  
  // Get device details using the correct API endpoint
  const deviceData = useQuery(api.devices.get.details, 
    deviceId ? { device: deviceId as any } : "skip"
  );
  
  // Use apps.list.all to get all apps
  const allAppsData = useQuery(api.apps.list.all, 
    {
      paginationOpts: {
        cursor: null,
        numItems: 1000
      }
    }
  );

  const isLoading = deviceData === undefined || allAppsData === undefined;

  console.log('[DeviceAppsAllAppsContent] Device data:', deviceData);
  console.log('[DeviceAppsAllAppsContent] All apps:', allAppsData?.page?.length || 0);

  const availableApps = allAppsData?.page || [];

  // Filter and sort apps alphabetically based on search query
  const filteredApps = useMemo(() => {
    let apps = availableApps;
    
    // Filter based on search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      apps = apps.filter((app: any) => 
        app.name?.toLowerCase().includes(query) ||
        app.bundleId?.toLowerCase().includes(query) ||
        app.category?.toLowerCase().includes(query) ||
        app.tags?.some((tag: string) => tag.toLowerCase().includes(query))
      );
    }
    
    // Sort alphabetically by app name
    return apps.sort((a: any, b: any) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }, [availableApps, searchQuery]);

  const getStatusBadge = (app: any) => {
    // Since the API handles all filtering, we can show a simple "Available" badge
    return <Badge className="bg-gray-100 text-gray-800">Available</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-600">Loading available apps...</span>
      </div>
    );
  }

  // Handle case where device has no deviceConfig
  if (!deviceData?.device?.deviceConfig) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Configuration</h3>
          <p className="text-gray-600 text-center mb-4 max-w-md">
            This device has no configuration. Please configure the device to see app settings.
          </p>
        </CardContent>
      </Card>
    );
  }


  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Search input */}
      <div className="w-full max-w-md">
        <AppSearchInput
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onCancel={() => setSearchQuery("")}
          placeholder="Search available apps..."
        />
      </div>

      {availableApps.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Apps Available</h3>
            <p className="text-gray-600 text-center mb-4 max-w-md">
              No apps are currently available for this device based on whitelist, blacklist, level, and category settings.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex-1 border rounded-lg overflow-hidden">
          <ScrollArea className="h-[calc(100vh-400px)]">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10 border-b">
                <TableRow className="h-10">
                  <TableHead className="w-8 sm:w-12 px-1 sm:px-3 py-2">Icon</TableHead>
                  <TableHead className="min-w-0 px-1 sm:px-3 py-2">App Name</TableHead>
                  <TableHead className="hidden sm:table-cell w-20 sm:w-24 px-1 sm:px-3 py-2">Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApps.map((app: any) => (
                  <TableRow key={app._id} className="h-12">
                    <TableCell className="px-1 sm:px-3 py-2">
                      {app.iconUrl ? (
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-50 rounded border border-gray-200 flex items-center justify-center overflow-hidden">
                          <img
                            src={app.iconUrl}
                            alt={app.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                          <Package className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-1 sm:px-3 py-2 min-w-0">
                      <div className="truncate">
                        <div className="font-medium text-sm truncate">{app.name}</div>
                        <div className="text-xs text-gray-500 sm:hidden truncate">
                          {app.category}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-1 sm:px-3 py-2 hidden sm:table-cell">
                      <span className="text-xs text-gray-600 truncate">{app.category}</span>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredApps.length === 0 && searchQuery && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-gray-500 text-sm">
                      No available apps found matching "{searchQuery}"
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
